import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import DatagridCustom from "../components/General/DatagridCustom";
import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import {
  getAuctioneers,
  createAuctioneer,
  updateAuctioneer,
  deleteAuctioneer,
} from "../services/AuctioneerService";

const EMPTY_FORM = { full_name: "", enrollment: "", phone: "", email: "", notes: "", is_active: true };

/**
 * Registro de martilleros. El nombre y la matrícula de acá son los que van
 * en los escritos ([MARTILLERO], [MATRICULA_MARTILLERO]).
 */
export default function Auctioneers() {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("administrar martilleros");

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAuctioneers({
        page: paginationModel.page + 1,
        perPage: paginationModel.pageSize,
        search: filterModel.quickFilterValues?.join(" ") || "",
      });
      setRows(res.data?.data ?? []);
      setTotal(res.data?.total ?? 0);
    } catch {
      showToast("No se pudieron cargar los martilleros.", "error");
    } finally {
      setLoading(false);
    }
  }, [paginationModel, filterModel, showToast]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const openCreate = () => {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      full_name: row.full_name ?? "",
      enrollment: row.enrollment ?? "",
      phone: row.phone ?? "",
      email: row.email ?? "",
      notes: row.notes ?? "",
      is_active: !!row.is_active,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const errors = {};
    if (!form.full_name.trim()) errors.full_name = "Escribí el nombre del martillero.";
    if (!form.enrollment.trim()) errors.enrollment = "Escribí la matrícula.";
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, email: form.email.trim() || null };
      const res = editRow ? await updateAuctioneer(editRow.id, payload) : await createAuctioneer(payload);
      showToast(res.data?.message ?? "Guardado.", "success");
      setDialogOpen(false);
      fetchRows();
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) {
        setFormErrors(Object.fromEntries(Object.entries(fieldErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v])));
      } else {
        showToast(err?.response?.data?.message ?? "No se pudo guardar.", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAuctioneer(rowToDelete.id);
      showToast("Martillero eliminado.", "success");
      setRowToDelete(null);
      fetchRows();
    } catch (err) {
      showToast(err?.response?.data?.message ?? "No se pudo eliminar.", "error");
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    { field: "full_name", headerName: "Nombre", flex: 1.6, minWidth: 200 },
    { field: "enrollment", headerName: "Matrícula", flex: 0.8, minWidth: 110 },
    { field: "phone", headerName: "Teléfono", flex: 0.9, minWidth: 120, valueGetter: (v) => v || "—" },
    { field: "email", headerName: "Email", flex: 1.2, minWidth: 160, valueGetter: (v) => v || "—" },
    {
      field: "legal_files_count",
      headerName: "Expedientes",
      flex: 0.7,
      minWidth: 110,
      valueGetter: (v) => v ?? 0,
    },
    {
      field: "is_active",
      headerName: "Estado",
      flex: 0.7,
      minWidth: 100,
      renderCell: ({ value }) => (
        <Chip
          label={value ? "Activo" : "Inactivo"}
          size="small"
          color={value ? "success" : "default"}
          variant="outlined"
          sx={{ fontWeight: 700 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      flex: 0.7,
      minWidth: 110,
      sortable: false,
      renderCell: ({ row }) =>
        canManage ? (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton size="small" onClick={() => openEdit(row)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => setRowToDelete(row)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null,
    },
  ];

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>
        <DatagridCustom
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          title="Martilleros"
          description="Registro de martilleros. El nombre y la matrícula de acá son los que van en los escritos."
          icon={<RecordVoiceOverRoundedIcon sx={{ fontSize: 32 }} />}
          headerActions={
            canManage && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openCreate}
                sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
              >
                Nuevo martillero
              </Button>
            )
          }
          paginationModel={paginationModel}
          onPaginationChange={setPaginationModel}
          filterModel={filterModel}
          onFilterChange={setFilterModel}
          filterMode="server"
          total={total}
          loading={loading}
          isServerPagination={true}
        />
      </Grid>

      <AppDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        title={editRow ? "Editar martillero" : "Nuevo martillero"}
        maxWidth="sm"
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
              sx={{ fontWeight: 800 }}
            >
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <TextField
            label="Nombre completo *"
            size="small"
            fullWidth
            autoFocus
            value={form.full_name}
            onChange={(e) => setField("full_name", e.target.value)}
            error={!!formErrors.full_name}
            helperText={formErrors.full_name ?? "Tal como tiene que salir en el escrito. Ej: EBBIO LEOPOLDO"}
          />
          <TextField
            label="Matrícula *"
            size="small"
            fullWidth
            value={form.enrollment}
            onChange={(e) => setField("enrollment", e.target.value)}
            error={!!formErrors.enrollment}
            helperText={
              formErrors.enrollment ??
              "Números, guion, números. Ej: 01-2011. Con esto se reconoce en las designaciones que se cargan."
            }
          />
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              label="Teléfono"
              size="small"
              sx={{ flex: 1, minWidth: 180 }}
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
            />
            <TextField
              label="Email"
              size="small"
              sx={{ flex: 1, minWidth: 180 }}
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Box>
          <TextField
            label="Notas"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.notes}
            onChange={(e) => setField("notes", e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch checked={form.is_active} onChange={(e) => setField("is_active", e.target.checked)} size="small" />
            }
            label={<Typography variant="body2">Activo (aparece para elegir en las designaciones)</Typography>}
          />
        </Box>
      </AppDialog>

      <ConfirmDialog
        open={Boolean(rowToDelete)}
        onClose={() => setRowToDelete(null)}
        onConfirm={handleDelete}
        title="Eliminar martillero"
        description={`¿Eliminar a "${rowToDelete?.full_name ?? ""}"? Si tiene expedientes asociados no se puede: desactivalo.`}
        confirmText="Eliminar"
        confirmColor="error"
        loading={deleting}
      />
    </Grid>
  );
}
